// src/pages/Register/RegisterPage.tsx
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';

interface Option {
  value: string;
  label: string;
}

const countryOptions: Option[] = [
  { value: 'Argentina', label: 'Argentina' },
  { value: 'Bolivia', label: 'Bolivia' },
];

const provincesOptions: Record<'Argentina' | 'Bolivia', Option[]> = {
  Argentina: [
    { value: 'Buenos Aires', label: 'Buenos Aires' },
    { value: 'Catamarca', label: 'Catamarca' },
    { value: 'Chaco', label: 'Chaco' },
    { value: 'Chubut', label: 'Chubut' },
    { value: 'Córdoba', label: 'Córdoba' },
    { value: 'Corrientes', label: 'Corrientes' },
    { value: 'Entre Ríos', label: 'Entre Ríos' },
    { value: 'Formosa', label: 'Formosa' },
    { value: 'Jujuy', label: 'Jujuy' },
    { value: 'La Pampa', label: 'La Pampa' },
    { value: 'La Rioja', label: 'La Rioja' },
    { value: 'Mendoza', label: 'Mendoza' },
    { value: 'Misiones', label: 'Misiones' },
    { value: 'Neuquén', label: 'Neuquén' },
    { value: 'Río Negro', label: 'Río Negro' },
    { value: 'Salta', label: 'Salta' },
    { value: 'San Juan', label: 'San Juan' },
    { value: 'San Luis', label: 'San Luis' },
    { value: 'Santa Cruz', label: 'Santa Cruz' },
    { value: 'Santa Fe', label: 'Santa Fe' },
    { value: 'Santiago del Estero', label: 'Santiago del Estero' },
    { value: 'Tierra del Fuego', label: 'Tierra del Fuego' },
    { value: 'Tucumán', label: 'Tucumán' },
  ],
  Bolivia: [
    { value: 'La Paz', label: 'La Paz' },
    { value: 'Santa Cruz', label: 'Santa Cruz' },
    { value: 'Cochabamba', label: 'Cochabamba' },
    { value: 'Oruro', label: 'Oruro' },
    { value: 'Potosí', label: 'Potosí' },
    { value: 'Chuquisaca', label: 'Chuquisaca' },
    { value: 'Tarija', label: 'Tarija' },
    { value: 'Beni', label: 'Beni' },
    { value: 'Pando', label: 'Pando' },
  ],
};

const phonePrefixOptions: Option[] = [
  { value: '+1', label: '+1 (USA/Canada)' },
  { value: '+7', label: '+7 (Rusia/Kazajistán)' },
  { value: '+20', label: '+20 (Egipto)' },
  { value: '+30', label: '+30 (Grecia)' },
  { value: '+31', label: '+31 (Países Bajos)' },
  { value: '+32', label: '+32 (Bélgica)' },
  { value: '+33', label: '+33 (Francia)' },
  { value: '+34', label: '+34 (España)' },
  { value: '+36', label: '+36 (Hungría)' },
  { value: '+39', label: '+39 (Italia)' },
  { value: '+41', label: '+41 (Suiza)' },
  { value: '+43', label: '+43 (Austria)' },
  { value: '+44', label: '+44 (Reino Unido)' },
  { value: '+45', label: '+45 (Dinamarca)' },
  { value: '+46', label: '+46 (Suecia)' },
  { value: '+47', label: '+47 (Noruega)' },
  { value: '+48', label: '+48 (Polonia)' },
  { value: '+49', label: '+49 (Alemania)' },
  { value: '+51', label: '+51 (Perú)' },
  { value: '+52', label: '+52 (México)' },
  { value: '+53', label: '+53 (Cuba)' },
  { value: '+54', label: '+54 (Argentina)' },
  { value: '+55', label: '+55 (Brasil)' },
  { value: '+56', label: '+56 (Chile)' },
  { value: '+57', label: '+57 (Colombia)' },
  { value: '+58', label: '+58 (Venezuela)' },
  { value: '+590', label: '+590 (Guadalupe)' },
  { value: '+591', label: '+591 (Bolivia)' },
  { value: '+593', label: '+593 (Ecuador)' },
  { value: '+594', label: '+594 (Guayana Francesa)' },
  { value: '+595', label: '+595 (Paraguay)' },
  { value: '+596', label: '+596 (Martinica)' },
  { value: '+597', label: '+597 (Surinam)' },
  { value: '+598', label: '+598 (Uruguay)' },
  { value: '+599', label: '+599 (Antillas Neerlandesas)' },
];

interface FormData {
  pais: 'Argentina' | 'Bolivia';
  provincia: string;
  localidad: string;
  nombre: string;
  apellido: string;
  rubro: string;
  email: string;
  phonePrefix: string;
  celular: string;
  hp_first: string;
  hp_last: string;
}

const RegisterPage = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [errorWhatsAppLink, setErrorWhatsAppLink] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formStartTime, setFormStartTime] = useState<number>(Date.now());
  const [formData, setFormData] = useState<FormData>({
    pais: 'Argentina',
    provincia: '',
    localidad: '',
    nombre: '',
    apellido: '',
    rubro: '',
    email: '',
    phonePrefix: '+54',
    celular: '',
    hp_first: '',
    hp_last: '',
  });

  // Guarda el tiempo de inicio al montar el componente
  useEffect(() => {
    setFormStartTime(Date.now());
  }, []);

  const validateForm = (): { [key: string]: string } => {
    const newErrors: { [key: string]: string } = {};
    if (formData.hp_first.trim() !== '') newErrors.hp_first = 'Invalid submission.';
    if (formData.hp_last.trim() !== '') newErrors.hp_last = 'Invalid submission.';
    if (Date.now() - formStartTime < 3000)
      newErrors.time = 'Completa el formulario manualmente, por favor.';
    if (!formData.pais) newErrors.pais = 'El país es requerido.';
    if (!formData.provincia) newErrors.provincia = 'Este campo es requerido.';
    if (!formData.localidad) newErrors.localidad = 'Este campo es requerido.';
    if (!formData.nombre) newErrors.nombre = 'El nombre es requerido.';
    if (!formData.apellido) newErrors.apellido = 'El apellido es requerido.';
    if (!formData.rubro) newErrors.rubro = 'El rubro es requerido.';
    if (!formData.email) {
      newErrors.email = 'El email es requerido.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido.';
    }
    if (!formData.celular) newErrors.celular = 'El número de teléfono es requerido.';
    return newErrors;
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const country = e.target.value as 'Argentina' | 'Bolivia';
    setFormData((prev) => ({
      ...prev,
      pais: country,
      provincia: '',
      localidad: '',
      phonePrefix: country === 'Argentina' ? '+54' : '+591',
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'provincia' ? { localidad: '' } : {}),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    const fullPhone = `${formData.phonePrefix}${formData.celular}`;
    const dataToSend = {
      pais: formData.pais,
      provincia: formData.provincia,
      localidad: formData.localidad,
      nombre: formData.nombre,
      apellido: formData.apellido,
      rubro: formData.rubro,
      email: formData.email,
      celular: fullPhone,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/registro/registro-usuario`,
        dataToSend,
      );
      if (response.status === 201) {
        toast({
          title: 'Registro enviado',
          description: `Se ha enviado un mensaje al celular ${fullPhone}. Continuaremos el registro por ahí.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setFormData({
          pais: 'Argentina',
          provincia: '',
          localidad: '',
          nombre: '',
          apellido: '',
          rubro: '',
          email: '',
          phonePrefix: '+54',
          celular: '',
          hp_first: '',
          hp_last: '',
        });
        setErrors({});
      }
    } catch (error) {
      let device = '007';
      if (formData.pais === 'Bolivia') {
        device = '001';
      } else if (
        formData.pais === 'Argentina' &&
        ['Salta', 'Chaco', 'Formosa', 'Jujuy'].includes(formData.provincia)
      ) {
        device = '001';
      }
      // Dispositivo 007 corresponde ahora a 5493525521008
      const whatsappNumber = device === '001' ? '5493525521001' : '5493525521008';
      const whatsappMessage = encodeURIComponent(
        `Hola! Me comunico para gestionar mi registro en NORPAN, estos son mis datos:
Nombre y Apellido: ${formData.nombre} ${formData.apellido}
Email: ${formData.email}
Celular: ${formData.phonePrefix}${formData.celular}
Rubro: ${formData.rubro}
País: ${formData.pais}
${formData.pais === 'Bolivia' ? 'Departamento' : 'Provincia'}: ${formData.provincia}
${formData.pais === 'Bolivia' ? 'Provincia/Municipio' : 'Localidad'}: ${formData.localidad}`,
      );
      const waLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;
      setErrorWhatsAppLink(waLink);
      onOpen();
    }
  };

  return (
    <Flex direction="column" align="center" p={4} bg="gray.100" minH="100vh">
      <Text fontSize="2xl" mb={2}>
        Registro de Usuario
      </Text>
      <Text mb={6} textAlign="center" maxW="500px" color="gray.600">
        👉 Accede a nuestra lista de precios, stock y promociones. Completa tus datos y te
        contactaremos para finalizar tu registro.
      </Text>
      {/* Honeypot inputs ocultos */}
      <Input
        name="hp_first"
        value={formData.hp_first}
        onChange={handleChange}
        tabIndex={-1}
        autoComplete="off"
        style={{ position: 'absolute', left: '-10000px' }}
      />
      <Input
        name="hp_last"
        value={formData.hp_last}
        onChange={handleChange}
        tabIndex={-1}
        autoComplete="off"
        style={{ position: 'absolute', left: '-10000px' }}
      />
      <Box
        width={{ base: '90%', md: '500px' }}
        as="form"
        onSubmit={handleSubmit}
        bg="white"
        p={6}
        borderRadius="md"
        boxShadow="md"
      >
        <FormControl mb={4} isRequired isInvalid={!!errors.pais}>
          <FormLabel>País</FormLabel>
          <Select name="pais" value={formData.pais} onChange={handleCountryChange}>
            {countryOptions.map((opt: Option) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          {errors.pais && <FormErrorMessage>{errors.pais}</FormErrorMessage>}
        </FormControl>
        <FormControl mb={4} isRequired isInvalid={!!errors.provincia}>
          <FormLabel>{formData.pais === 'Bolivia' ? 'Departamento' : 'Provincia'}</FormLabel>
          <Select
            name="provincia"
            value={formData.provincia}
            onChange={handleChange}
            placeholder={
              formData.pais === 'Bolivia'
                ? 'Seleccione un departamento'
                : 'Seleccione una provincia'
            }
          >
            {provincesOptions[formData.pais].map((opt: Option) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          {errors.provincia && <FormErrorMessage>{errors.provincia}</FormErrorMessage>}
        </FormControl>
        <FormControl mb={4} isRequired isInvalid={!!errors.localidad}>
          <FormLabel>{formData.pais === 'Bolivia' ? 'Provincia/Municipio' : 'Localidad'}</FormLabel>
          <Input
            name="localidad"
            value={formData.localidad}
            onChange={handleChange}
            placeholder={
              formData.pais === 'Bolivia' ? 'Ingrese provincia o municipio' : 'Ingrese localidad'
            }
            required
          />
          {errors.localidad && <FormErrorMessage>{errors.localidad}</FormErrorMessage>}
        </FormControl>
        <FormControl mb={4} isRequired isInvalid={!!errors.nombre}>
          <FormLabel>Nombre</FormLabel>
          <Input
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            placeholder="Ingrese su nombre"
            required
          />
          {errors.nombre && <FormErrorMessage>{errors.nombre}</FormErrorMessage>}
        </FormControl>
        <FormControl mb={4} isRequired isInvalid={!!errors.apellido}>
          <FormLabel>Apellido</FormLabel>
          <Input
            name="apellido"
            value={formData.apellido}
            onChange={handleChange}
            placeholder="Ingrese su apellido"
            required
          />
          {errors.apellido && <FormErrorMessage>{errors.apellido}</FormErrorMessage>}
        </FormControl>
        <FormControl mb={4} isRequired isInvalid={!!errors.rubro}>
          <FormLabel>Rubro</FormLabel>
          <Input
            name="rubro"
            value={formData.rubro}
            onChange={handleChange}
            placeholder="Ingrese rubro"
            required
          />
          {errors.rubro && <FormErrorMessage>{errors.rubro}</FormErrorMessage>}
        </FormControl>
        <FormControl mb={4} isRequired isInvalid={!!errors.email}>
          <FormLabel>Email</FormLabel>
          <Input
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Ingrese su email"
            type="email"
            required
          />
          {errors.email && <FormErrorMessage>{errors.email}</FormErrorMessage>}
        </FormControl>
        <FormControl mb={4} isRequired isInvalid={!!errors.celular}>
          <FormLabel>Celular</FormLabel>
          <Flex>
            <Select
              name="phonePrefix"
              value={formData.phonePrefix}
              onChange={handleChange}
              width={{ base: '60%', md: '20%' }}
            >
              {phonePrefixOptions.map((opt: Option) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            <Input
              name="celular"
              value={formData.celular}
              onChange={handleChange}
              placeholder={formData.pais === 'Argentina' ? 'Ej: 9112345678' : 'Ej: 71234567'}
              type="tel"
              pattern={formData.pais === 'Argentina' ? '^[0-9]{10}$' : '^[0-9]{8}$'}
              required
              width={{ base: '40%', md: '70%' }}
              maxLength={formData.pais === 'Argentina' ? 10 : 8}
            />
          </Flex>
          {errors.celular && <FormErrorMessage>{errors.celular}</FormErrorMessage>}
          {errors.time && <FormErrorMessage>{errors.time}</FormErrorMessage>}
          {errors.hp_first && <FormErrorMessage>{errors.hp_first}</FormErrorMessage>}
          {errors.hp_last && <FormErrorMessage>{errors.hp_last}</FormErrorMessage>}
        </FormControl>
        <Button
          type="submit"
          bg="#B23E8F"
          color="white"
          width="100%"
          mb={4}
          _hover={{ bg: '#9e3372' }}
        >
          Enviar Registro
        </Button>
      </Box>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Registro pendiente</ModalHeader>
          <ModalBody>
            <Text>
              Gracias! Haz click en el Botón de Whatsapp para enviarnos los datos de registro
              ingresados y continuar tu alta.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button bg="#B23E8F" color="white" mr={3} onClick={onClose} _hover={{ bg: '#9e3372' }}>
              Cancelar
            </Button>
            <Button as="a" href={errorWhatsAppLink} target="_blank" colorScheme="green">
              Ir a WhatsApp
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default RegisterPage;
